// Weltformat: eine Welt ist ein Raster aus Chunk-Säulen, jeder Chunk ein Bytearray mit einer
// Block-id je Block. Reine Daten, kein Bezug zum Rendering. Dorf und spätere Hof-Parzellen
// nutzen dasselbe Format.

import { blockDef, type BlockId } from './blocks.js';

/** Kantenlänge eines Chunks in x und z. */
export const CHUNK_SIZE = 16;
/** Höhe der Welt in y; ein Chunk ist eine Säule 16 × 32 × 16. */
export const WORLD_HEIGHT = 32;
/** Blöcke je Chunk, zugleich Länge von `Chunk.blocks`. */
export const CHUNK_BLOCK_COUNT = CHUNK_SIZE * WORLD_HEIGHT * CHUNK_SIZE;

export interface Chunk {
  cx: number;
  cz: number;
  /** Eine Block-id je Block, Index = blockIndex(x, y, z) mit Chunk-lokalen Koordinaten. */
  blocks: Uint8Array;
}

export interface World {
  chunksX: number;
  chunksZ: number;
  /** Index = cz * chunksX + cx. */
  chunks: Chunk[];
}

/** Weltgrenzen in Blöcken; `maxX`/`maxZ` sind die Außenkanten (exklusiv), siehe ADR 0002. */
export interface WorldBounds {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
  height: number;
}

/** Index eines Blocks innerhalb eines Chunks nach dem Vertrag: (y * 16 + z) * 16 + x. */
export function blockIndex(x: number, y: number, z: number): number {
  return (y * CHUNK_SIZE + z) * CHUNK_SIZE + x;
}

/** Umkehrung von `blockIndex`: Chunk-lokale Koordinaten zu einem Index. */
export function localFromIndex(index: number): { x: number; y: number; z: number } {
  const x = index % CHUNK_SIZE;
  const rest = (index - x) / CHUNK_SIZE;
  const z = rest % CHUNK_SIZE;
  const y = (rest - z) / CHUNK_SIZE;
  return { x, y, z };
}

/** Legt eine leere Welt (nur Luft) aus chunksX × chunksZ Chunks an. */
export function createWorld(chunksX: number, chunksZ: number): World {
  const chunks: Chunk[] = [];
  for (let cz = 0; cz < chunksZ; cz++) {
    for (let cx = 0; cx < chunksX; cx++) {
      chunks.push({ cx, cz, blocks: new Uint8Array(CHUNK_BLOCK_COUNT) });
    }
  }
  return { chunksX, chunksZ, chunks };
}

/** Der Chunk an Chunk-Koordinaten (cx, cz) oder `undefined` außerhalb. */
export function getChunk(world: World, cx: number, cz: number): Chunk | undefined {
  if (cx < 0 || cz < 0 || cx >= world.chunksX || cz >= world.chunksZ) return undefined;
  return world.chunks[cz * world.chunksX + cx];
}

/**
 * Löst Weltkoordinaten in Chunk und Index innerhalb des Chunks auf.
 * Gleitkommawerte werden auf den Block abgerundet, in dem der Punkt liegt.
 * Liefert `undefined` außerhalb der Welt.
 */
function locate(
  world: World,
  x: number,
  y: number,
  z: number,
): { chunk: Chunk; index: number } | undefined {
  const bx = Math.floor(x);
  const by = Math.floor(y);
  const bz = Math.floor(z);
  if (by < 0 || by >= WORLD_HEIGHT) return undefined;
  const chunk = getChunk(world, Math.floor(bx / CHUNK_SIZE), Math.floor(bz / CHUNK_SIZE));
  if (chunk === undefined) return undefined;
  const index = blockIndex(bx - chunk.cx * CHUNK_SIZE, by, bz - chunk.cz * CHUNK_SIZE);
  return { chunk, index };
}

/** Block-id an Weltkoordinaten; außerhalb der Welt 0 (Luft). Den Weltrand begrenzt die Simulation. */
export function getBlock(world: World, x: number, y: number, z: number): BlockId {
  const ort = locate(world, x, y, z);
  if (ort === undefined) return 0;
  return ort.chunk.blocks[ort.index] ?? 0;
}

/** Setzt die Block-id an Weltkoordinaten; außerhalb der Welt keine Wirkung. */
export function setBlock(world: World, x: number, y: number, z: number, id: BlockId): void {
  const ort = locate(world, x, y, z);
  if (ort === undefined) return;
  ort.chunk.blocks[ort.index] = id;
}

/** Ob der Block an Weltkoordinaten laut Palette fest ist; außerhalb der Welt false. */
export function isSolid(world: World, x: number, y: number, z: number): boolean {
  const ort = locate(world, x, y, z);
  if (ort === undefined) return false;
  return blockDef(ort.chunk.blocks[ort.index] ?? 0).solid;
}

/** Weltgrenzen in Blöcken: x und z von 0 bis zur Außenkante, y von 0 bis `height`. */
export function worldBounds(world: World): WorldBounds {
  return {
    minX: 0,
    minZ: 0,
    maxX: world.chunksX * CHUNK_SIZE,
    maxZ: world.chunksZ * CHUNK_SIZE,
    height: WORLD_HEIGHT,
  };
}
